import React, { useEffect, useState } from 'react';
import { Form, Input, Select, Button, message, Card, Result } from 'antd';
import { UserOutlined, PhoneOutlined, MailOutlined, ShopOutlined, DownOutlined } from '@ant-design/icons';
import { apiInstance } from '../../utils/api';
import { logUserAction } from '../../utils/logger';
import './TurnoverContactCreate.scss';

const { Option } = Select;

const TurnoverContactCreate = () => {
  const [form] = Form.useForm();
  const [commercialUnits, setCommercialUnits] = useState([]);
  const [loadingUnits, setLoadingUnits] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  useEffect(() => {
    const fetchUnits = async () => {
      setLoadingUnits(true);
      try {
        const response = await apiInstance(
          `${process.env.REACT_APP_API_URL}/api/public/commercial-units`,
          'GET'
        );
        setCommercialUnits(Array.isArray(response) ? response : []);
      } catch (err) {
        console.error('Помилка завантаження торгових точок:', err);
        message.error('Не вдалося завантажити список торгових точок');
      } finally {
        setLoadingUnits(false);
      }
    };
    fetchUnits();
  }, []);

  const handleSubmit = async (values) => {
    setSubmitting(true);
    try {
      const selectedUnit = commercialUnits.find((u) => u.Id === values.commercialUnitId);
      const payload = {
        fullName: values.fullName,
        phone: values.phone,
        email: values.email,
        commercialUnitId: values.commercialUnitId,
        commercialUnitName: selectedUnit ? selectedUnit.RespNameCU : '',
      };

      await apiInstance(
        `${process.env.REACT_APP_API_URL}/api/public/create-turnover-contact`,
        'POST',
        payload
      );

      logUserAction({
        action: 'CREATE_TURNOVER_CONTACT_SUCCESS',
        level: 'info',
        message: `Створено контакт товарообігу: ${values.fullName}`,
        storeCrmId: values.commercialUnitId,
        details: { fullName: values.fullName, email: values.email },
      });

      setSubmitted(true);
      message.success('Контакт товарообігу успішно створено');
    } catch (err) {
      console.error('Помилка збереження контакту:', err);
      logUserAction({
        action: 'CREATE_TURNOVER_CONTACT_ERROR',
        level: 'error',
        message: err?.message || 'Помилка під час створення контакту',
        storeCrmId: values.commercialUnitId,
      });
      message.error(err?.message || 'Помилка під час створення контакту');
    } finally {
      setSubmitting(false);
    }
  };


  return (
    <div className="turnover-contact-page">
      <div className="turnover-contact-header">
        <img src="/images/logo.svg" alt="Республіка" className="turnover-contact-logo" />
        <h1 className="turnover-contact-title">Реєстрація контакту товарообігу</h1>
        <p className="turnover-contact-subtitle">
          Заповніть дані співробітника та оберіть торгову точку для надання доступу
        </p>
      </div>

      <Card className="turnover-contact-card">
        {submitted ? (
          <Result
            status="success"
            title="Контакт успішно створено!"
            subTitle="Співробітника зареєстровано та прив'язано до обраної торгової точки."
            extra={[
              <Button
                type="primary"
                key="again"
                onClick={() => {
                  form.resetFields();
                  setSubmitted(false);
                }}
                style={{ background: '#9A18FF', borderColor: '#9A18FF', borderRadius: '8px' }}
              >
                Додати ще один контакт
              </Button>,
            ]}
          />
        ) : (
          <Form form={form} layout="vertical" onFinish={handleSubmit} requiredMark={false}>
            <Form.Item
              name="fullName"
              label="ПІБ співробітника"
              validateTrigger={['onSubmit', 'onChange']}
              rules={[
                { required: true, message: 'Будь ласка, введіть ПІБ співробітника' },
                {
                  validator: (_, value) => {
                    if (!value) return Promise.resolve();
                    const words = value.trim().split(/\s+/).filter(Boolean);
                    if (words.length === 3) {
                      return Promise.resolve();
                    }
                    return Promise.reject(
                      new Error('ПІБ має складатися рівно з 3 слів (Прізвище, Ім\'я, По батькові)')
                    );
                  },
                },
              ]}
            >
              <Input
                prefix={<UserOutlined style={{ color: '#8c8c8c' }} />}
                placeholder="Шевченко Тарас Григорович"
                size="large"
              />
            </Form.Item>

            <Form.Item
              name="phone"
              label="Номер телефону"
              validateTrigger={['onSubmit', 'onChange']}
              rules={[
                { required: true, message: 'Будь ласка, введіть номер телефону' },
                {
                  validator: (_, value) => {
                    if (!value) {
                      return Promise.resolve();
                    }
                    if (!value.startsWith('+38')) {
                      return Promise.reject(new Error('Номер повинен починатися з +38'));
                    }
                    if (value.length < 13) {
                      return Promise.reject(new Error('Номер повинен містити 12 цифр (+380XXXXXXXXX)'));
                    }
                    const operatorCode = value.substring(3, 6);
                    const VALID_OPERATOR_CODES = [
                      '039', '050', '063', '066', '067', '068', '073', '077', '089',
                      '091', '092', '093', '094', '095', '096', '097', '098', '099'
                    ];
                    if (!VALID_OPERATOR_CODES.includes(operatorCode)) {
                      return Promise.reject(new Error('Невірний код оператора'));
                    }
                    return Promise.resolve();
                  },
                },
              ]}
            >
              <Input
                prefix={<PhoneOutlined style={{ color: '#8c8c8c' }} />}
                placeholder="+380961234567"
                size="large"
                maxLength={13}
                onKeyPress={(e) => {
                  const allowedChars = /^[0-9+]$/;
                  if (!allowedChars.test(e.key)) {
                    e.preventDefault();
                  }
                }}
              />
            </Form.Item>

            <Form.Item
              name="email"
              label="Електронна пошта"
              rules={[
                { required: true, message: 'Будь ласка, введіть електронну пошту' },
                { type: 'email', message: 'Некоректний формат електронної пошти' },
              ]}
            >
              <Input
                prefix={<MailOutlined style={{ color: '#8c8c8c' }} />}
                placeholder="example@domain.com"
                size="large"
              />
            </Form.Item>

            <Form.Item
              name="commercialUnitId"
              label="Торгова точка"
              rules={[{ required: true, message: 'Будь ласка, оберіть торгову точку' }]}
            >
              <Select
                showSearch
                suffixIcon={<DownOutlined style={{ color: '#8c8c8c' }} />}
                loading={loadingUnits}
                placeholder="Введіть назву торгової точки..."
                size="large"
                style={{ width: '100%' }}
                notFoundContent={loadingUnits ? 'Завантаження...' : 'За вашим запитом не знайдено торгової точки'}
                optionFilterProp="children"
                filterOption={(input, option) =>
                  String(option?.children || '')
                    .toLowerCase()
                    .includes(input.toLowerCase())
                }
              >
                {commercialUnits.map((unit) => (
                  <Option key={unit.Id} value={unit.Id}>
                    {unit.RespNameCU || 'Без назви'}
                  </Option>
                ))}
              </Select>
            </Form.Item>

            <Form.Item style={{ marginTop: '24px', marginBottom: 0 }}>
              <Button
                type="primary"
                htmlType="submit"
                loading={submitting}
                block
                size="large"
                icon={<ShopOutlined />}
                style={{
                  background: '#9A18FF',
                  borderColor: '#9A18FF',
                  height: '48px',
                  borderRadius: '8px',
                  fontSize: '16px',
                  fontWeight: 600,
                }}
              >
                Створити контакт товарообігу
              </Button>
            </Form.Item>
          </Form>
        )}
      </Card>
    </div>
  );
};

export default TurnoverContactCreate;
